import styled from '@emotion/styled';
import {
  palette,
  gradient,
  radius,
  shadow,
  spacing,
  transition,
  duration,
  easing,
  layout,
  space,
  typography,
  zIndex,
} from '../../../style-utils/theme';
import { up, down, touch } from '../../../style-utils/media-queries';
import { ScrollStrip } from '../../scroll/scroll-affordance';
import { ActiveIndicator, m } from '../../../motion';

export const LogoContainer = styled.div`
  height: ${layout.navHeightDesktop};
  min-height: ${layout.navHeightDesktop};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${spacing.sm};

  ${up('lg')} {
    padding: 0 ${spacing.md};
  }

  ${down('md')} {
    display: none;
  }
`;

export const LogoButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs};
  min-height: ${layout.touchTarget};
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: ${radius.sm};

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

/* Brand chip — flat rounded square holding the "L" monogram. Solid iris, NOT a
   gradient: any iris→irisGlow sweep reads as the aurora AI mark, so the brand chip
   stays flat and aurora belongs to Lobbero IQ alone (design_system §gradient). */
export const LogoSpan = styled.span`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: ${radius.sm};
  background: ${palette.iris};
  display: flex;
  text-align: center;
  justify-content: center;
  align-items: center;
  font-family: ${typography.display};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.bold};
  line-height: 1;
  color: ${palette.white};
  box-shadow: ${shadow.brand};
  margin-right: ${spacing.half};
`;

/* Wordmark tail ("obbero") — sets the whole "Lobbero" mark as one display unit. */
export const LogoWordmark = styled.span`
  font-family: ${typography.display};
  /* IT NEVER SET A SIZE, so the browser's default for a button's contents — a
     bare 13.3333px — was the size of the wordmark on every dashboard screen.
     18px matches the 32px brand chip it sits beside. */
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.bold};
  letter-spacing: ${typography.tracking.tight};
  color: ${palette.ink};
  line-height: 1;
`;

export const MenuAndLogoutWrappper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${spacing.lg} ${spacing.sm};

  /* THE LAST DESTINATION WAS UNREACHABLE AT 200% TEXT ZOOM.
     Measured by scripts/wp/probe-wp33-bottomnav.js with TEXT2X=1: at 1024px the
     rail's ninth item ended 130px PAST the rail's bottom edge, and the probe's
     own answer to "what scrolls this?" was NONE — so Billing simply could not be
     reached on a desk by anyone who enlarges text. Flagged in WP-33 as
     pre-existing and left without an owner through four sessions.

     The page itself does not scroll here (document 1024/1024), which is why no
     width check ever saw it: the overflow is on the OTHER axis, inside a column
     that had no overflow rule at all. min-height: 0 is the other half — a flex
     child will not shrink below its content without it, so overflow-y alone
     would never have engaged. */
  min-height: 0;
  overflow-y: auto;

  ${down('md')} {
    flex: 1;
    min-width: 0;
    flex-direction: row;
    align-items: center;
    padding: ${spacing.xs} ${spacing.sm};
  }
`;

export const MenuContainer = styled.div`
  ${down('md')} {
    display: flex;
    align-items: center;
    gap: ${spacing.xs};
    min-width: 0;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

/* WP-33. The frame that carries the edge shade for the strip above.
 *
 * ScrollStrip is WP-34's, shared rather than reinvented - the bottom bar is the
 * same shape as the three dashboard strips: overflow-x: auto with the scrollbar
 * deliberately suppressed and, until now, nothing put in its place. Wrapping
 * rather than replacing means MenuContainer keeps the layout it was designed
 * with and only gains the signal.
 *
 * The flex rule is why this exists at all: the frame sits where MenuContainer
 * used to sit inside a row that is `flex: 1; min-width: 0`, and a wrapper that
 * does not shrink would push the More button off the bar. NO BACKTICKS IN THIS
 * COMMENT - it is inside a styled template literal. */
export const MenuStrip = styled(ScrollStrip)`
  min-width: 0;

  ${down('md')} {
    flex: 1 1 auto;
  }
`;

/* The fifth control on the bar: everything that did not fit in four.
 *
 * DESKTOP NEVER SEES IT. The rail is a vertical column with room for nine, so
 * the split is a phone rule and only a phone rule - which is also why the
 * overflow entries stay in the DOM and are hidden by CSS rather than being
 * conditionally rendered: one tree, two presentations, and no JS width query
 * that can disagree with the media query beside it. */
export const MoreButton = styled.button<{ active?: boolean }>`
  display: none;

  ${down('md')} {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: ${spacing.half};
    min-height: ${layout.touchTarget};
    padding: ${spacing.xs};
    border: none;
    border-radius: ${radius.sm};
    cursor: pointer;
    font: inherit;
    font-size: ${typography.size.base}px;
    font-weight: ${typography.weight.medium};
    white-space: nowrap;
    color: ${({ active }) => (active ? palette.lagoon : palette.slate)};
    background: ${({ active }) => (active ? palette.irisTint : 'transparent')};
    transition: ${transition.colors};

    &:focus-visible {
      outline: none;
      box-shadow: ${shadow.focus};
    }
  }
`;

export const DashboardLogout = styled.div`
  ${down('md')} {
    display: none;
  }
`;

export const MenuTitle = styled.div`
  margin-bottom: ${spacing.sm};
  padding: 0 ${spacing.sm};

  ${down('md')} {
    display: none;
  }
`;

/* Nav item — active = iris-tint pill and icon tint; the 3px accent bar is
   RailIndicator below, ONE element that travels between items rather than a
   pseudo-element redrawn on each. Font weight stays constant (never reflow);
   state is colour/bg. */
export const MenuItem = styled.button<{ active?: boolean; railOnly?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  /* WP-33. Was ml={8} on the label itself, which is a margin that exists for
     the icon beside it - and that icon is display:none below md, so on a
     phone every one of nine items carried 8px of space for something that was
     not there. A gap contributes nothing when there is only one child, so the
     same rule now says the same thing on the rail and says nothing on the bar.
     NO BACKTICKS IN THIS COMMENT - it is inside a styled template literal. */
  gap: ${spacing.xs};
  min-height: ${layout.touchTarget};
  margin-bottom: ${spacing.xs};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.sm};
  cursor: pointer;
  width: 100%;
  border: none;
  font: inherit;
  text-align: left;
  color: ${({ active }) => (active ? palette.lagoon : palette.slate)};
  background: ${({ active }) => (active ? palette.irisTint : 'transparent')};
  transition:
    ${transition.colors},
    transform ${duration.fast} ${easing.standard};

  & svg {
    flex-shrink: 0;
    opacity: ${({ active }) => (active ? 1 : 0)};
    transition:
      transform ${duration.fast} ${easing.standard},
      opacity ${duration.fast} ${easing.standard};
  }

  &:hover {
    background: ${({ active }) =>
      active ? palette.irisTint : palette.irisTintSoft};
    color: ${palette.lagoon};
  }

  &:hover svg {
    opacity: 1;
    transform: translateX(2px);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  ${down('md')} {
    /* THE OVERFLOW ENTRIES LIVE IN THE More SHEET ON A PHONE, not in the bar
       (owner ruling R-13). They stay in the tree so the vertical rail above md
       is unchanged - same items, same order, same permission filter. */
    display: ${({ railOnly }) => (railOnly ? 'none' : 'flex')};
    flex: 0 0 auto;
    width: auto;
    max-width: ${layout.navItemMaxWidth};
    margin-bottom: 0;
    /* One grid step of side padding instead of two. On the rail the wider box
       is the pill the reader points at; on the bar it is 16px per item of a
       320px budget, and the bar is the one place in the app where horizontal
       space is genuinely scarce. The tap target is unaffected - min-height
       carries that, and the narrowest label is still far wider than 44px. */
    padding-left: ${spacing.xs};
    padding-right: ${spacing.xs};
    white-space: nowrap;

    & svg {
      display: none;
    }

    /* The label is the text carrier, so this is where a too-long name has to be
       cut. text-overflow needs a block box with its own overflow - it does
       nothing on the flex container above it, which is what "no overflow rule"
       looked like in the audit. */
    & > span {
      display: block;
      min-width: 0;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

/* THE ONE ACCENT BAR. Rendered inside whichever MenuItem is current; framer's
   layoutId carries it to the next item with the indicator spring, so the bar
   SLIDES between destinations instead of shrinking on one and growing on the
   next. Positioned with top/left arithmetic rather than a translate, because
   the layout animation owns this element's transform. Under md the rail is the
   fixed bottom bar, and the bar becomes the 24x3 underline of the active slot.
   NO BACKTICKS IN THIS COMMENT - it is inside a styled template literal. */
export const RailIndicator = styled(ActiveIndicator)`
  position: absolute;
  left: 0;
  top: calc(50% - (${spacing.md} / 2));
  width: 3px;
  height: ${spacing.md};
  border-radius: ${radius.pill};
  background: ${palette.iris};
  pointer-events: none;

  ${down('md')} {
    left: calc(50% - 12px);
    top: auto;
    bottom: 0;
    width: 24px;
    height: 3px;
  }
`;

export const LogOutButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${spacing.xs};
  min-height: ${layout.touchTarget};
  margin-bottom: ${spacing.sm};
  padding: ${spacing.xs} ${spacing.sm};
  cursor: pointer;
  border: none;
  border-radius: ${radius.sm};
  background: transparent;
  transition:
    ${transition.colors},
    transform ${duration.fast} ${easing.standard};

  &:hover {
    background: ${palette.irisTintSoft};
  }

  &:active {
    transform: scale(0.97);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

/* Header — frosted sticky surface with a crisp 1px hairline (not an
   invisible 3%-alpha shadow); elevation reads from the translucent blur. */
export const DashboardHeadingContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: ${zIndex.sticky};
  height: ${layout.navHeightDesktop};
  min-height: ${layout.navHeightDesktop};
  /* Right padding carries an extra reserve: the NotificationDock is fixed to
     the top-right corner and is not in this header's flow, so without it the
     bell lands on top of the profile avatar. layout.bellDockReserve is the one
     number both sides agree on — see theme.ts. */
  padding: 0 calc(${spacing.sm} + ${layout.bellDockReserve}) 0 ${spacing.sm};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.sm};
  background: ${palette.porcelain};
  border-bottom: 1px solid ${palette.line};

  ${up('md')} {
    padding: 0 calc(${spacing.md} + ${layout.bellDockReserve}) 0 ${spacing.md};
  }
  ${up('lg')} {
    padding: 0 calc(${spacing.lg} + ${layout.bellDockReserve}) 0 ${spacing.lg};
  }
`;

/* The heading's title text. A motion span so DashboardHeading can crossfade
   it (keyed by title, inside a Presence) when the section changes, while the
   h1 around it stays put. */
export const HeadingTitleSwap = m.span`
  display: inline-block;
`;

export const SearchAndProfileContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${spacing.sm};
`;

export const SearchInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;

  &:focus-within > span {
    color: ${palette.lagoon};
  }
`;

/* ── THE LIST SCREENS' HEADER ROW (Buildings, Users) ───────────────────────
   The title, then the controls as ONE group: search, filters, add button.

   They used to be siblings of the title in a row that could not wrap from md
   up, and the only sibling able to give way was the title - a Text, which
   carries min-width 0 and overflow-wrap: anywhere so user data can never widen
   the page. So the title is what shrank. Measured at 1280: "Buildings" 41px
   wide as "Buil / ding / s", and 12px with a long portfolio name widening its
   select; "Users" 37px as "Use / rs".

   Grouped, the row wraps as TWO things. The title keeps its own width (each
   listing says so); when the group does not fit beside it, the whole group
   drops under it, still right-aligned, and wraps between its own controls if
   it has to. gap: inherit keeps each listing's own spacing inside the group.

   Below md the group is not a box at all (display: contents): its controls lay
   out in the listing exactly as they did before it existed - stacked on
   Buildings, wrapped on Users. */
export const ListingControls = styled.div`
  display: contents;

  ${up('md')} {
    display: flex;
    flex: 1 1 auto;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: inherit;
    min-width: 0;
  }
`;

/* The search box's share of that group: a basis and a cap. SearchInputContainer
   on its own is width: 100%, which in a wrapping group claims a line of its
   own. */
export const ListingSearch = styled(SearchInputContainer)`
  ${up('md')} {
    flex: 1 1 ${space(25)};
    max-width: ${space(40)};
  }
`;

/* Leading search glyph inside the pill. */
export const SearchIconWrapper = styled.span`
  position: absolute;
  left: ${spacing.sm};
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  color: ${palette.slateLight};
  pointer-events: none;
  transition: color ${duration.fast} ${easing.standard};
`;

export const StyledSearchInput = styled.input<{ isReportsFilter?: boolean }>`
  box-sizing: border-box;
  height: ${layout.controlHeight};
  width: 100%;
  max-width: ${({ isReportsFilter }) => (isReportsFilter ? '100%' : '320px')};
  padding: 0 88px 0 ${spacing.xl};
  border-radius: ${radius.pill};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  box-shadow: ${shadow.xs};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  transition:
    ${transition.colors},
    max-width ${duration.base} ${easing.entrance};

  ::placeholder {
    color: ${palette.slateLight};
  }

  &:hover {
    border-color: ${palette.lineStrong};
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
    max-width: ${({ isReportsFilter }) => (isReportsFilter ? '100%' : '400px')};
  }
`;

export const StyledSearchButton = styled.button`
  position: absolute;
  top: 50%;
  right: ${spacing.half};
  transform: translateY(-50%);
  height: ${layout.touchTarget};
  padding: 0 ${spacing.sm};
  border: none;
  border-radius: ${radius.pill};
  background: ${gradient.primary};
  color: ${palette.white};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  box-shadow: ${shadow.brand};
  transition:
    ${transition.colors},
    box-shadow ${duration.base} ${easing.standard},
    transform ${duration.instant} ${easing.spring};

  &:hover {
    background: ${gradient.primaryHover};
    box-shadow: ${shadow.brandHover};
    transform: translateY(-50%) scale(1.02);
  }

  &:active {
    transform: translateY(-50%) scale(0.97);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const LogedInUser = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;

  ${down('sm')} {
    display: none;
  }
`;

/* Profile avatar ring — brand-tinted, lifts + iris ring on hover. */
export const ProfileButtonBorder = styled.div`
  /* content-box, and it is load-bearing. The avatar INSIDE this ring is itself
     layout.controlHeight square; under border-box the 2px ring ate into that
     box, the inner button lost 4px of width but kept its height, and the
     "circle" rendered as an egg. content-box makes the ring sit outside the
     44px it is drawing around. */
  box-sizing: content-box;
  width: ${layout.controlHeight};
  height: ${layout.controlHeight};
  min-width: ${layout.controlHeight};
  min-height: ${layout.controlHeight};
  /* A header is a flex row; without this the ring is the first thing to give
     when the row runs short, and it gives on ONE axis, which is the other way
     a circle turns into an ellipse. */
  flex-shrink: 0;
  border-radius: ${radius.circle};
  border: 2px solid ${palette.irisTint};
  background: ${palette.irisTintSoft};
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: ${transition.colors};

  /* No hover transform here: the ProfileModal is an absolute descendant, and
     a transform would re-anchor its containing block to this 44px circle —
     the dropdown visibly jumps on every hover. Border + shadow only. */
  &:hover {
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

/* Content canvas — warm porcelain, generous 16/24/32 rhythm, and the single
   desktop scroll owner for dashboard routes. Individual screens own any local
   entrance motion so route changes do not stack animations. */
/* THE COLUMN SCROLLS SIDEWAYS ON THE LIST SCREENS, AND SAID NOTHING.
   Measured by scripts/analytics/responsive.js on an iPad Mini (768): Staff
   Access scrollWidth 1082 in a 575 box, Users 591 in 575, Units 309 over. The
   PAGE does not scroll — this column absorbs it — which is exactly why WP-37's
   own measurement was green and stayed green: it checked the document, and this
   is one level in.

   The table is not the thing to change. TableFrame is overflow: visible on
   purpose (list-table.tsx), because overflow-x: auto clips the row action menus
   — measured 45px past the frame on Users, 69 on Buildings, 213 on Portfolios.
   And the card reflow that would make these fit is under md, so a device that is
   EXACTLY 768 keeps the wide table by one pixel.

   So the column carries the signal instead: the same end fade the tab strip
   uses, driven by the same useScrollEdges, on the ref dashboard.tsx already
   holds for its scroll-to-top. NO BACKTICKS IN THIS COMMENT - styled literal. */
export const DashboardContentContainer = styled.div<{ $fadeEnd?: boolean }>`
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  overflow: auto;

  /* The mask is read for its ALPHA: opaque means keep, transparent means fade.
     32px, the same width as the tab strip's and WP-34's ScrollShade. */
  ${({ $fadeEnd }) =>
    $fadeEnd
      ? `-webkit-mask-image: linear-gradient(to right, black 0, black calc(100% - 32px), transparent 100%);
         mask-image: linear-gradient(to right, black 0, black calc(100% - 32px), transparent 100%);`
      : ''}
  background: ${palette.porcelain};
  padding: ${spacing.sm};

  /* ROOM AT THE END FOR THE FLOATING LAUNCHER.
     The IQ launcher is position:fixed, so it occupies the bottom-right corner of
     every dashboard screen without taking any space in this scroller. On a list
     that happens to end near the fold — /dashboard/buildings at 768 — the last
     row's Actions button sat under it with NO scroll room left, which made it
     unpressable rather than merely tight. See layout.fabReserve. */
  padding-bottom: ${layout.fabReserve};

  ${up('md')} {
    padding: ${spacing.md};
    padding-bottom: ${layout.fabReserve};
  }
  ${up('lg')} {
    padding: ${spacing.lg};
    padding-bottom: ${layout.fabReserve};
  }

  ${down('md')} {
    overflow: visible;
  }
`;

// The one-time portfolio hint (owner decision Q-5). Sits directly under the
// Portfolios item the moment that item first appears, and never again after it
// is dismissed. Deliberately quiet: this explains a capability that just became
// available, it is not an announcement and it must not compete with the nav.
export const MenuHint = styled.div`
  /* TOKENS, NOT LITERALS (WP-18). This carried margin 4/8/12, padding 10/12,
     gap 6 and a raw 8px radius — four values off the 8pt grid, and two of them
     (10 and 6) not on any scale at all. It also concatenated hex alpha onto a
     palette colour, which design_system.md:75-76 forbids at a call site: the
     resulting colour exists in no token, so nothing can keep it in step with
     the palette and a theme change silently leaves it behind.
     NO BACKTICKS IN THIS COMMENT: it is inside a styled template literal. */
  margin: ${spacing.half} ${spacing.xs} ${spacing.sm} ${spacing.xs};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.xs};
  background: ${palette.irisTintSoft};
  border: 1px solid ${palette.line};
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};

  /* WP-33 / RESP-RESP-04. OUT OF THE FLEX ROW ENTIRELY BELOW md.
     This is a column block holding a two-sentence paragraph and a dismiss
     button, rendered as a sibling of the Portfolios item - which is fine in a
     vertical rail and is a defect in a horizontal one: it has no
     flex 0 0 auto, so in the bar it collapsed to its longest word and grew
     downward inside a 72px overflow-hidden container. The Got it button went
     below the bar's edge, so the one-time hint could never be dismissed on a
     phone and permanently wedged a paragraph between two nav destinations.
     MenuTitle two rules up is the precedent. Portfolios is an overflow entry on
     a phone under R-13 anyway, so the hint would have had nothing to sit under.
     NO BACKTICKS IN THIS COMMENT - it is inside a styled template literal. */
  ${down('md')} {
    display: none;
  }
`;

export const MenuHintDismiss = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  /* 30x18 was the word alone (the 768 sweep). 24px under a mouse, the WCAG
     2.5.8 floor the audit scanner holds every control to; the 44px touch
     floor on touch. NO BACKTICKS IN THIS COMMENT: styled template literal. */
  min-height: ${spacing.md};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;

  ${touch} {
    min-height: ${layout.touchTarget};
    min-width: ${layout.touchTarget};
  }
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: ${palette.lagoon};

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${palette.iris};
    outline-offset: 2px;
  }
`;

/* ───────────────────  WP-33: the More sheet (phones only)  ─────────────────
 *
 * R-13 caps the bar at four destinations. The rest are not removed - they move
 * behind one control, and this is where they land. It is a bottom sheet rather
 * than a popover because it is opened from the very bottom edge of a phone
 * screen, where a popover would open upward into the content it is covering.
 *
 * PORTALLED, and it has to be: the bar is `position: fixed` with
 * `overflow: hidden` and now a `backdrop-filter` as well, which makes it a
 * containing block AND a stacking context. A sheet rendered inside it would be
 * clipped to 72px by the same rule that clipped the portfolio hint.
 */

/* Motion elements: the fade and the rise come from scrimMotion / the 'sheet'
   overlay preset spread on them in nav-more-sheet.tsx, and because they are
   rendered inside a Presence they also animate OUT. */
export const NavSheetScrim = m.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.overlay};
  background: ${palette.overlay};
`;

export const NavSheet = m.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${zIndex.modal};
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  max-height: 80vh;
  max-height: 80dvh;
  overflow-y: auto;
  padding: ${spacing.sm};
  /* The home indicator on a notched phone sits inside the sheet otherwise. */
  padding-bottom: calc(${spacing.xl} + var(--safe-bottom));
  background: ${palette.surface};
  border-radius: ${radius.lg} ${radius.lg} 0 0;
  box-shadow: ${shadow.xl};
`;

export const NavSheetHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  padding: 0 ${spacing.xs} ${spacing.xs};
`;

export const NavSheetClose = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: ${layout.touchTarget};
  min-height: ${layout.touchTarget};
  padding: 0 ${spacing.xs};
  border: none;
  border-radius: ${radius.sm};
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.lagoon};

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const NavSheetItem = styled.button<{ active?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: ${layout.touchTarget};
  padding: ${spacing.xs} ${spacing.sm};
  border: none;
  border-radius: ${radius.sm};
  cursor: pointer;
  font: inherit;
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.medium};
  text-align: left;
  color: ${({ active }) => (active ? palette.lagoon : palette.slate)};
  background: ${({ active }) => (active ? palette.irisTint : 'transparent')};
  transition: ${transition.colors};

  &:hover {
    background: ${({ active }) => (active ? palette.irisTint : palette.irisTintSoft)};
    color: ${palette.lagoon};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;
